import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CreditCard, Banknote, CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModulePaymentDialog = ({ open, onOpenChange, selectedModules, totalAmount, onSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePayment = async () => {
    if (paymentMethod === 'ORANGE_MONEY' && (!phoneNumber || !phoneNumber.startsWith('+224'))) {
      toast.error('Veuillez entrer un numéro Orange Money valide (+224...)');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Souscrire à chaque module sélectionné avec la méthode de paiement
      for (const moduleId of selectedModules) {
        await axios.post(
          `${API}/tenant/modules/subscribe`,
          { 
            module_id: moduleId,
            payment_method: paymentMethod,
            phone_number: paymentMethod === 'ORANGE_MONEY' ? phoneNumber : undefined
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (paymentMethod === 'ORANGE_MONEY') {
        toast.success('Paiement Orange Money réussi ! Modules activés immédiatement.');
      } else {
        toast.success('Demande de souscription envoyée. En attente de validation par le Super Admin.');
      }
      
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors du paiement';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Méthode de Paiement</DialogTitle>
          <DialogDescription>
            Choisissez votre méthode de paiement pour activer {selectedModules.length} module(s)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Résumé */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {selectedModules.length} module(s) sélectionné(s)
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {formatPrice(totalAmount)} GNF/mois
              </div>
            </div>
          </div>

          {/* Méthodes de paiement */}
          <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
            <div className="space-y-3">
              {/* Orange Money */}
              <div className={`flex items-center space-x-3 border rounded-lg p-4 cursor-pointer transition-all ${paymentMethod === 'ORANGE_MONEY' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="ORANGE_MONEY" id="orange_money" />
                <Label htmlFor="orange_money" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-8 bg-orange-500 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-sm">OM</span>
                    </div>
                    <div>
                      <div className="font-medium">Orange Money</div>
                      <div className="text-xs text-gray-500">Paiement instantané</div>
                    </div>
                  </div>
                </Label>
                <CheckCircle2 className={`w-5 h-5 ${paymentMethod === 'ORANGE_MONEY' ? 'text-orange-500' : 'text-gray-300'}`} />
              </div>

              {/* Espèces */}
              <div className={`flex items-center space-x-3 border rounded-lg p-4 cursor-pointer transition-all ${paymentMethod === 'CASH' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="CASH" id="cash" />
                <Label htmlFor="cash" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Banknote className="w-8 h-8 text-green-600" />
                    <div>
                      <div className="font-medium">Espèces</div>
                      <div className="text-xs text-gray-500">Validation manuelle requise</div>
                    </div>
                  </div>
                </Label>
                <CheckCircle2 className={`w-5 h-5 ${paymentMethod === 'CASH' ? 'text-green-500' : 'text-gray-300'}`} />
              </div>

              {/* Chèque */}
              <div className={`flex items-center space-x-3 border rounded-lg p-4 cursor-pointer transition-all ${paymentMethod === 'CHEQUE' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="CHEQUE" id="cheque" />
                <Label htmlFor="cheque" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-8 h-8 text-blue-600" />
                    <div>
                      <div className="font-medium">Chèque</div>
                      <div className="text-xs text-gray-500">Validation manuelle requise</div>
                    </div>
                  </div>
                </Label>
                <CheckCircle2 className={`w-5 h-5 ${paymentMethod === 'CHEQUE' ? 'text-blue-500' : 'text-gray-300'}`} />
              </div>
            </div>
          </RadioGroup>

          {/* Champ Orange Money */}
          {paymentMethod === 'ORANGE_MONEY' && (
            <div className="space-y-2">
              <Label htmlFor="phone">Numéro Orange Money *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+224 6XX XX XX XX"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500">
                Format: +224 6XX XX XX XX (ex: +224 621 23 45 67)
              </p>
            </div>
          )}

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              onClick={handlePayment}
              disabled={loading || (paymentMethod === 'ORANGE_MONEY' && !phoneNumber)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Traitement...
                </>
              ) : (
                `Confirmer (${formatPrice(totalAmount)} GNF/mois)`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModulePaymentDialog;
